import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { LEGACY_CHART_TYPES } from './fabric-mnemonic.properties';
import { buildMnemonicImageUrl, extractMnemonicFileNameFromSrc, stripAuthTokenFromUrl } from './fabric-image-url.builder';
import { parseMnemonicCanvasData } from './fabric-canvas.parser';
import { isSerializedImageType, resolveMnemonicImageId } from './fabric-object.enricher';
import { MnemonicCanvasLoadOptions } from './fabric-canvas.types';

function resolveMnemonicImageLoadSrc(object: Record<string, unknown>, options: MnemonicCanvasLoadOptions): string | undefined {
    const imageId = resolveMnemonicImageId(object);
    const mnemonicImage = options.mnemonicImages?.find((image) => image.id === imageId);

    if (mnemonicImage?.fileName) {
        return buildMnemonicImageUrl(mnemonicImage.fileName);
    }

    if (typeof object.src !== 'string' || !object.src) {
        return undefined;
    }

    if (object.src.includes('/api/virtual-devices/mnemonic-file/')) {
        const fileName = extractMnemonicFileNameFromSrc(object.src);

        if (fileName) {
            return buildMnemonicImageUrl(fileName);
        }

        return stripAuthTokenFromUrl(object.src);
    }

    return stripAuthTokenFromUrl(object.src);
}

function normalizeMnemonicImageSrc(object: Record<string, unknown>, options: MnemonicCanvasLoadOptions): Record<string, unknown> {
    if (!isSerializedImageType(object)) {
        return object;
    }

    const src = resolveMnemonicImageLoadSrc(object, options);

    return {
        ...object,
        type: ObjectTypeEnum.image,
        ...(src ? { src } : {}),
        objectType: object.objectType === ObjectTypeEnum.defaultImage ? object.objectType : ObjectTypeEnum.image,
    };
}

function normalizeMnemonicObjectForLoad(object: Record<string, unknown>, options: MnemonicCanvasLoadOptions): Record<string, unknown> {
    let normalized: Record<string, unknown> = { ...object };

    if (typeof normalized.type === 'string' && LEGACY_CHART_TYPES.has(normalized.type)) {
        normalized = {
            ...normalized,
            type: 'chart',
        };
    }

    if (normalized.type === ObjectTypeEnum.gauge || normalized.objectType === ObjectTypeEnum.gauge) {
        normalized = {
            ...normalized,
            type: 'gauge',
            objectType: ObjectTypeEnum.gauge,
        };
    }

    if (Array.isArray(normalized.objects)) {
        normalized = {
            ...normalized,
            objects: normalized.objects.map((child) => normalizeMnemonicObjectForLoad(child as Record<string, unknown>, options)),
        };
    }

    return normalizeMnemonicImageSrc(normalized, options);
}

export function prepareMnemonicCanvasForLoad(raw: unknown, options: MnemonicCanvasLoadOptions = {}): Record<string, unknown> | null {
    const parsed = parseMnemonicCanvasData(raw);
    if (!parsed) {
        return null;
    }

    const objects = Array.isArray(parsed.objects) ? parsed.objects : [];

    return {
        ...parsed,
        objects: objects.map((object) => normalizeMnemonicObjectForLoad(object as Record<string, unknown>, options)),
        backgroundImage: parsed.backgroundImage
            ? normalizeMnemonicImageSrc(parsed.backgroundImage as Record<string, unknown>, options)
            : parsed.backgroundImage,
    };
}

export { resolveMnemonicImageLoadSrc };
